import { MapPin, CheckCircle, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";

const ServiceAreasSection = () => {
  const serviceAreas = [
    {
      name: "Tucson",
      slug: "/tucson-stump-grinding",
      description: "Greater Tucson metropolitan area including central, midtown, east, and south Tucson neighborhoods.",
    },
    {
      name: "Oro Valley",
      slug: "/oro-valley-stump-grinding",
      description: "Oro Valley and Northwest Tucson communities including Rancho Vistoso and Catalina Foothills.",
    },
    {
      name: "Catalina",
      slug: "/catalina-stump-grinding",
      description: "Catalina, Catalina Foothills, and the surrounding mountain communities.",
    },
    {
      name: "Sahuarita",
      slug: "/sahuarita-stump-grinding",
      description: "Sahuarita and Green Valley areas in the Santa Cruz Valley south of Tucson.",
    },
    {
      name: "SaddleBrooke",
      slug: "/saddlebrooke-stump-grinding",
      description: "SaddleBrooke retirement community and neighboring areas north of Catalina.",
    },
    {
      name: "Oracle",
      slug: "/oracle-stump-grinding",
      description: "Oracle, San Manuel, and surrounding areas near the Santa Catalina Mountains.",
    },
  ];

  return (
    <section
      id="areas"
      className="py-16 lg:py-24 relative overflow-hidden"
      data-testid="service-areas-section"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center parallax-bg"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/2559941/pexels-photo-2559941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
        }}
      />
      <div className="absolute inset-0 bg-[#1A1A1A]/90" />
      <div className="absolute inset-0 noise-bg" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-left mb-12 lg:mb-16">
          <div className="accent-line mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase mb-4">
            Stump Grinding Service Areas Near Me
          </h2>
          <p className="font-body text-lg text-gray-300 max-w-2xl">
            Proudly serving Tucson, Arizona and surrounding communities with local stump grinding and tree stump removal. If you don't see your area listed, give us a call — we may still be able to help!
          </p>
        </div>

        {/* Areas Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceAreas.map((area, index) => (
            <Link
              key={area.name}
              to={area.slug}
              className="bg-white border-2 border-white p-6 transition-all duration-200 hover:border-[#FF4D00] group block"
              data-testid={`service-area-${area.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF4D00] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1A1A1A] transition-colors">
                  <MapPin className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-[#1A1A1A] uppercase mb-2">
                    {area.name}, AZ
                  </h3>
                  <p className="font-body text-sm text-gray-600 leading-relaxed">
                    {area.description}
                  </p>
                  <span className="inline-block mt-3 font-heading text-sm font-bold text-[#FF4D00] uppercase group-hover:underline">
                    Learn More →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Coverage Note */}
        <div className="mt-12 bg-[#FF4D00] p-6 lg:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <h3 className="font-heading text-xl sm:text-2xl font-bold text-white uppercase mb-2">
                Don't See Your Area?
              </h3>
              <p className="font-body text-white/90">
                We frequently serve areas beyond our main service zones. Contact us for availability in your location.
              </p>
            </div>
            <a
              href="tel:+15202007739,2"
              className="bg-[#1A1A1A] text-white px-8 py-4 font-heading font-bold uppercase tracking-wide hover:bg-black transition-colors flex-shrink-0"
              data-testid="areas-call-btn"
            >
              Call (520) 200-7739 Ext 2
            </a>
          </div>
        </div>

        {/* Service Promise */}
        <div className="mt-12 grid sm:grid-cols-3 gap-6">
          {[
            { text: "Fast Response Times", subtext: "Usually within 24 hours" },
            { text: "Free On-Site Estimates", subtext: "No obligation quotes" },
            { text: "Local Tucson Business", subtext: "Serving Arizona since 2014" },
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-[#FF4D00] flex-shrink-0" />
              <div>
                <span className="font-heading font-bold text-white uppercase text-sm block">
                  {item.text}
                </span>
                <span className="font-body text-gray-400 text-sm">{item.subtext}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServiceAreasSection;
